import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { mainNav, siteConfig } from "@/mocks/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/today?q=${encodeURIComponent(q)}` : "/today");
    setOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-1 py-2 text-sm transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-700 font-semibold"
        : "text-foreground-700 hover:text-foreground-950"
    }`;

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden md:flex h-8 items-center justify-between px-4 md:px-6 bg-primary-950 text-background-100 text-xs tracking-wide">
        <p className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-400 anim-pulse-dot"></span>
          今日看点已更新 · 每日 08:00 / 20:00 两次更新
        </p>
        <p className="hidden lg:block text-background-200/80">
          线索与投稿：{siteConfig.contactEmail}
        </p>
      </div>

      <div className="bg-background-50/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-baseline gap-0.5 shrink-0" aria-label={`${siteConfig.name} 首页`}>
            <span className="font-brand text-2xl md:text-[26px] leading-none text-foreground-950">
              {siteConfig.brandMark}
            </span>
            <span className="font-brand text-2xl md:text-[26px] leading-none text-primary-600">
              {siteConfig.brandMarkAccent}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" aria-label="主导航">
            {mainNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={submitSearch} className="hidden lg:flex items-center h-9 rounded-full border border-background-300 bg-background-100 pl-3 pr-1 focus-within:border-primary-300">
              <i className="ri-search-line text-foreground-500 text-sm"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索看点 / 关键词"
                aria-label="搜索看点"
                className="w-40 xl:w-52 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 px-2 outline-none"
              />
              <button
                type="submit"
                className="h-7 px-3 rounded-full bg-foreground-950 text-background-50 text-xs whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>

            <Link
              to="/today"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-fire-line"></i>
              今日必看
            </Link>

            <button
              type="button"
              aria-label={open ? "关闭菜单" : "打开菜单"}
              aria-expanded={open}
              onClick={() => setOpen((prev) => !prev)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-900 cursor-pointer"
            >
              <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-6">
            <form
              onSubmit={submitSearch}
              className="flex items-center h-11 rounded-md border border-background-300 bg-background-100 pl-3 pr-1 mb-5"
            >
              <i className="ri-search-line text-foreground-500"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索看点 / 关键词"
                aria-label="搜索看点"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 px-2 outline-none"
              />
              <button
                type="submit"
                className="h-8 px-3 rounded-md bg-foreground-950 text-background-50 text-xs whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <nav className="flex flex-col" aria-label="移动端主导航">
              {mainNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-3.5 border-b border-background-200 text-base ${
                      isActive ? "text-primary-700 font-semibold" : "text-foreground-800"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </NavLink>
              ))}
            </nav>

            <Link
              to="/today"
              onClick={() => setOpen(false)}
              className="mt-6 flex items-center justify-center gap-2 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
            >
              <i className="ri-fire-line"></i>
              查看今日必看看点
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}